﻿import { useEffect, useMemo, useState } from 'react';
import { TApiApplicationDocument, StatusFilterValue } from '~/modules/covenants/types.ts';
import {
  buildApplicationDocumentRowKey,
  buildApplicationDocumentStableKey,
  getRowId,
} from '~/modules/covenants/utils.ts';

export const useApplicationDocumentRows = ({
  applicationId,
  applicationDocuments,
}: {
  applicationId?: string;
  applicationDocuments: TApiApplicationDocument[];
}) => {
  const [documentRows, setDocumentRows] = useState<TApiApplicationDocument[]>([]);

  const buildVirtualRow = (document: TApiApplicationDocument): TApiApplicationDocument => ({
    ...document,
    id: undefined,
    rowId: getRowId(document),
    isVirtualRow: true,
    fileName: undefined,
    comment: undefined,
    createdAt: undefined,
    isCustomSignFile: false,
    hasSignedFile: false,
    signedByName: undefined,
    signetByCode: undefined,
    status: StatusFilterValue.New,
  });

  useEffect(() => {
    setDocumentRows((prev) => {
      const currentPageGroupKeys = new Set(
        applicationDocuments.map((document) => buildApplicationDocumentStableKey(document))
      );

      const persistedRows = prev.filter((document) => {
        if (document.isVirtualRow) {
          return true;
        }

        return !currentPageGroupKeys.has(buildApplicationDocumentStableKey(document));
      });

      return [...persistedRows, ...applicationDocuments];
    });
  }, [applicationDocuments]);

  useEffect(() => {
    if (!applicationId) {
      return;
    }

    setDocumentRows([]);
  }, [applicationId]);

  const handleAddDocumentRow = (document: TApiApplicationDocument) => {
    const groupKey = buildApplicationDocumentStableKey(document);

    setDocumentRows((prev) => {
      const hasPendingVirtualRow = prev.some(
        (candidate) =>
          candidate.isVirtualRow && buildApplicationDocumentStableKey(candidate) === groupKey
      );

      if (hasPendingVirtualRow) {
        return prev;
      }

      return [...prev, buildVirtualRow(document)];
    });
  };

  const handleRemoveVirtualRow = (rowKey: string) => {
    setDocumentRows((prev) =>
      prev.filter(
        (document) => !document.isVirtualRow || buildApplicationDocumentRowKey(document) !== rowKey
      )
    );
  };

  const visibleDocumentRows = useMemo(() => {
    const currentPageGroupKeys = Array.from(
      new Set(applicationDocuments.map((document) => buildApplicationDocumentStableKey(document)))
    );

    return currentPageGroupKeys.flatMap((groupKey) =>
      documentRows
        .filter((document) => buildApplicationDocumentStableKey(document) === groupKey)
        .sort((left, right) => Number(!!left.isVirtualRow) - Number(!!right.isVirtualRow))
    );
  }, [applicationDocuments, documentRows]);

  return {
    documentRows,
    visibleDocumentRows,
    handleAddDocumentRow,
    handleRemoveVirtualRow,
  };
};
